import { useEffect, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { applyUpdate, pwaStore } from '../../lib/pwa';
import { useStore } from '../../lib/store';
import { takeVersionChange } from '../../lib/version';
import { player } from '../../player/engine';
import { Icon } from '../icons/Icon';
import { Button, toast } from '../primitives';
import { versionLabel } from '../features/settings/settingsLogic';
import { curves, seconds } from '../theme/motion';
import { reportError } from './errorReporter';
import styles from './UpdatePrompt.module.css';

/**
 * "New version" prompt (PWA strength kept from the original app). Hidden
 * while audio plays so an update never interrupts listening; Update stops the
 * player first, which saves the position, because applying reloads the page.
 * The prompt names the incoming version, and the first launch of a new
 * version says which one it is, so every update is visible.
 */
/** Gap between the prompt and a toast stacked under it. */
const TOAST_GAP_PX = 8;

/**
 * While the prompt is up, toasts drop in below it (`--toast-top-offset` on
 * `<html>`, read by the toast host) instead of covering it; the room follows
 * the card's height as it wraps.
 */
function useToastOffset(card: HTMLElement | null, active: boolean): void {
  useEffect(() => {
    const root = document.documentElement;
    if (!card || !active) {
      root.style.removeProperty('--toast-top-offset');
      return;
    }
    const apply = () => root.style.setProperty('--toast-top-offset', `${Math.ceil(card.getBoundingClientRect().height) + TOAST_GAP_PX}px`);
    apply();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(apply);
    observer?.observe(card);
    return () => {
      observer?.disconnect();
      root.style.removeProperty('--toast-top-offset');
    };
  }, [card, active]);
}

export function UpdatePrompt() {
  const ready = useStore(pwaStore, (s) => s.updateReady);
  const nextVersion = useStore(pwaStore, (s) => s.availableVersion);
  const playing = useStore(player.store, (s) => s.isPlaying);
  const reduceMotion = useReducedMotion();
  const [dismissed, setDismissed] = useState(false);
  const [updating, setUpdating] = useState(false);
  const visible = ready && !playing && !dismissed;
  const [card, setCard] = useState<HTMLDivElement | null>(null);
  useToastOffset(card, visible);

  useEffect(() => {
    const change = takeVersionChange();
    if (change) toast.success(`Plume updated to ${versionLabel(change.to)}.`);
  }, []);

  // On success the page reloads; a failure re-enables the button and says so.
  const update = async () => {
    setUpdating(true);
    try {
      if (player.state.loaded) await player.stop();
      await applyUpdate();
    } catch (error) {
      reportError(error, 'applyUpdate', 'pwa');
      toast.error('Unable to update Plume. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className={styles.host}>
      <AnimatePresence>
        {visible && (
          <motion.div
            ref={setCard}
            className={styles.card}
            role="status"
            // As the toasts: drops in (opacity easeOutCubic, slide easeOutBack) and leaves upwards; a plain fade with reduced motion.
            initial={{ opacity: 0, y: reduceMotion ? 0 : '-45%' }}
            animate={{
              opacity: 1,
              y: 0,
              transition: reduceMotion
                ? { duration: seconds.quick, ease: curves.easeOut }
                : { duration: seconds.toastIn, ease: curves.easeOutBack, opacity: { duration: seconds.toastIn, ease: curves.easeOutCubic } },
            }}
            exit={{
              opacity: 0,
              y: reduceMotion ? 0 : '-45%',
              transition: { duration: reduceMotion ? seconds.quick : seconds.toastOut, ease: curves.easeInCubic },
            }}
          >
            <span className={styles.iconTile} aria-hidden>
              <Icon name="arrow_down_circle_fill" size={18} />
            </span>
            <span className={styles.message}>{nextVersion ? `Plume ${versionLabel(nextVersion)} is ready.` : 'A new version of Plume is ready.'}</span>
            <span className={styles.actions}>
              <Button variant="text" tone="neutral" onClick={() => setDismissed(true)} disabled={updating}>
                Later
              </Button>
              <Button onClick={() => void update()} loading={updating} disabled={updating}>
                Update
              </Button>
            </span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
